import { useState } from 'react'
import { exportToXlsx, type ExportColumn } from '@/lib/exportXlsx'
import { toast } from '@/components/ui/Toast'

interface Service<T> {
  list: (params?: Record<string, unknown>) => Promise<{ data: T[]; meta?: { total?: number } }>
}

interface Options<T> {
  /** Nome base do arquivo exportado (sem extensão). */
  filename: string
  /** Service que será chamado em "Todos os registros" — usa size grande. */
  service: Service<T>
  /** Filtros atuais (busca, status, etc.) — replicados no export "tudo". */
  filters?: Record<string, unknown>
  /** Colunas a exportar — pode diferir das colunas visuais. */
  columns: ExportColumn<T>[]
}

/**
 * Hook utilitário para alimentar os callbacks `onExportPage` e `onExportAll`
 * da `DataTable`.
 *
 *   const exp = useDataTableExport({ filename: 'subscribers', service: SubscribersService, filters, columns: EXPORT_COLS })
 *   <DataTable exportable onExportPage={() => exp.exportPage(rows)} onExportAll={exp.exportAll} />
 */
export function useDataTableExport<T>({ filename, service, filters, columns }: Options<T>) {
  const [busy, setBusy] = useState(false)

  /** Exporta os registros já carregados na tela. */
  function exportPage(rows: T[]) {
    if (!rows.length) {
      toast.error('Nenhum registro para exportar.')
      return
    }
    exportToXlsx(filename, rows, columns)
  }

  /** Busca tudo do backend respeitando filtros atuais e exporta. */
  async function exportAll() {
    if (busy) return
    setBusy(true)
    try {
      // Tenta size=0 (alguns endpoints retornam tudo). Fallback: 5000.
      let res = await service.list({ ...(filters ?? {}), size: 0 })
      if (!res.data?.length) {
        res = await service.list({ ...(filters ?? {}), size: 5000 })
      }
      if (!res.data?.length) {
        toast.error('Nenhum registro para exportar.')
        return
      }
      exportToXlsx(filename, res.data, columns)
    } catch {
      toast.error('Erro ao exportar registros.')
    } finally {
      setBusy(false)
    }
  }

  return { exportPage, exportAll, busy }
}
